/**
 * PROPOSTA E-MAIL — o que a TELA e o SERVIDOR precisam dizer igual
 * (Douglas 05/10/2026).
 *
 * O consultor escreve direto na prévia do e-mail: a abertura (acima da imagem
 * da proposta) e o fechamento (abaixo dela). O resto — imagem, botão,
 * assinatura e moldura — sai do servidor. Este arquivo é puro (sem banco, sem
 * rede) porque o front importa daqui: a régua do texto e o aviso de valor são
 * uma só nos dois lados.
 */

export type MetodoEnvioEmail = 'smtp' | 'sendgrid' | 'ambos';

export const METODOS_ENVIO_EMAIL: readonly MetodoEnvioEmail[] = ['smtp', 'sendgrid', 'ambos'];

export const ROTULO_METODO_EMAIL: Record<MetodoEnvioEmail, string> = {
  smtp: 'SMTP',
  sendgrid: 'SendGrid',
  ambos: 'SMTP + SendGrid',
};

export const LIMITE_ASSUNTO_EMAIL = 200;
export const LIMITE_TEXTO_EMAIL = 6000;

/** Onde a tela encaixa os textos editáveis e a imagem na prévia. */
export const MARCA_ABERTURA = '%%ABERTURA%%';
export const MARCA_FECHAMENTO = '%%FECHAMENTO%%';
export const MARCA_IMAGEM = '%%IMAGEM_SRC%%';

export const FECHAMENTO_PADRAO_PROPOSTA = 'Qualquer dúvida é só me chamar no WhatsApp.';

function escapar(x: string): string {
  return x.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Normaliza o que a pessoa digitou: quebras de linha unix e sem espaço sobrando. */
export function normalizarTextoEmail(texto: unknown): string {
  return String(texto ?? '').replace(/\r\n?/g, '\n').replace(/ /g, ' ').replace(/[ \t]+\n/g, '\n').trim();
}

/** Linha em branco separa parágrafo; quebra simples vira <br>. */
export function textoParaHtmlEmail(texto: string): string {
  return normalizarTextoEmail(texto)
    .split(/\n{2,}/)
    .map((par) => par.trim())
    .filter(Boolean)
    .map((par) => `<p style="margin:0 0 14px 0;">${escapar(par).replace(/\n/g, '<br/>')}</p>`)
    .join('\n');
}

function numeroBR(txt: string): number {
  const limpo = txt.includes(',') ? txt.replace(/\./g, '').replace(',', '.') : txt;
  return Number(limpo);
}

function brl(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export interface ReferenciaValoresProposta {
  /** Total novo da proposta (o que o cliente vai pagar). */
  valorNovo: number;
  /** Todo valor que existe na proposta e pode ser citado no texto. */
  valores: number[];
  /** GB total novo. */
  gbNovo: number;
  /** Todo GB que existe na proposta e pode ser citado no texto. */
  gbs: number[];
}

/**
 * O texto é livre, mas valor e GB que não existem na proposta viram AVISO —
 * não bloqueio. O cliente vai ler o texto E a imagem: se o texto diz R$ 49,90 e
 * a imagem R$ 54,99, ele cobra o que leu. Quem decide é o consultor.
 */
export function avisoValoresDoTexto(texto: string, ref: ReferenciaValoresProposta): string | null {
  const bate = (lista: number[], v: number) => lista.some((x) => Number.isFinite(x) && Math.abs(x - v) < 0.01);

  for (const m of String(texto || '').matchAll(/R\$\s*(\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?|\d+(?:,\d{1,2})?)/gi)) {
    const v = numeroBR(m[1]);
    if (!Number.isFinite(v) || v <= 0) continue;
    if (!bate(ref.valores, v)) {
      return `Você escreveu ${brl(v)} no texto, mas a proposta está em ${brl(ref.valorNovo)}. O cliente vai ler os dois. Pode enviar, mas confira.`;
    }
  }
  for (const m of String(texto || '').matchAll(/(\d+(?:[.,]\d+)?)\s*(?:GB|giga)/gi)) {
    const g = numeroBR(m[1]);
    if (!Number.isFinite(g) || g <= 0) continue;
    if (!bate(ref.gbs, g)) {
      return `Você escreveu ${m[1]} GB no texto, mas a proposta tem ${ref.gbNovo} GB. O cliente vai ler os dois. Pode enviar, mas confira.`;
    }
  }
  return null;
}

/**
 * A PROPOSTA MONTADA VENCE A PRÉVIA DO TOP (Douglas 24/10/2026: "quando o
 * consultor tiver proposta montada, o e-mail sai com ela; a prévia do TOP só
 * quando não houver proposta montada"). Em 24/10 a imagem dizia 3 × R$ 39,99 e
 * o e-mail levava a prévia do TOP, 3 × R$ 54,99 da Pré-Black.
 */
export function temPropostaMontada(p: { valor_novo?: number | null; servicos?: unknown[] | null }): boolean {
  return (Number(p.valor_novo) || 0) > 0 || (p.servicos?.length ?? 0) > 0;
}
